import { Box, Tooltip } from '@mui/material';
import {
    FrameAxis,
    useFrameRotationReadout,
} from '~/hooks/viewer/useFrameRotationReadout';
import { FrameRotationSource } from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { READOUT, READOUT_FONT } from '../chrome';
import FrameResetButton from './FrameResetButton';

interface FrameAngleReadoutProps {
    /** The oblique frame the tile's row shares. */
    readonly source: FrameRotationSource;
    /** Returns the row's tiles to their patient-aligned planes. */
    readonly onReset: () => void;
    /** The patient axis whose turn this readout shows. */
    readonly axis: FrameAxis;
}

/**
 * How far the row's frame is turned about one patient axis, and one click
 * back to the patient-aligned planes. Nothing is shown while that turn
 * reads 0.0° at one decimal, so an aligned row carries no readout.
 */
export default function FrameAngleReadout({
    source,
    onReset,
    axis,
}: FrameAngleReadoutProps) {
    useChangeVersion(source);
    const readout = useFrameRotationReadout(source.getFrameRotation());
    const angle = readout?.angles[axis];
    if (!readout || angle === undefined) {
        return null;
    }
    return (
        <>
            <Tooltip title={readout.anglesTooltip} placement="bottom">
                <Box
                    component="span"
                    data-testid="tile-frame-angle"
                    role="status"
                    sx={{
                        fontFamily: READOUT_FONT,
                        fontSize: 11,
                        color: READOUT,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}
                >
                    {angle}
                </Box>
            </Tooltip>
            <FrameResetButton label={readout.snapLabel} onReset={onReset} />
        </>
    );
}
